import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { usePdfDownloader } from "../common/usePdfDownloader";
import { GLOBALTYPES } from "../redux/actions/globalTypes";
import { getDataAPI, postDataAPI } from "../utils/fetchData";
import Adminnavbar from "./adminnavbar";
import Footer from "./footer";
import LoginRequest from "./loginRequest";
import Sidenavbar from "./sidenav";

// Constants
const CONTAINER_STYLE = { backgroundColor: "#ffffff", padding: "30px" };
const DASHBOARD_ROW_STYLE = { paddingTop: "20px", paddingBottom: "20px" };
const DASHBOARD_COL_STYLE = { backgroundColor: "#A7D588" };
const STATS_CARD_STYLE = { backgroundColor: "#24A2B3" };
const ACCEPTED_CARD_STYLE = { backgroundColor: "#E96CB4" };
const DECLINED_CARD_STYLE = { backgroundColor: "#7F2DA2" };
const MALE_CARD_STYLE = { backgroundColor: "#C78B62" };
const FEMALE_CARD_STYLE = { backgroundColor: "#4C87B8" };
const PENDING_CARD_STYLE = { backgroundColor: "#8572D6" };
const ANALYTICS_CARD_STYLE = { backgroundColor: "#A7D588" };
const ANALYTICS_ROW_STYLE = { paddingBottom: "20px", paddingTop: "20px" };
const ANALYTICS_BOTTOM_ROW_STYLE = { paddingBottom: "20px" };
const TABLE_CONTAINER_STYLE = { marginTop: "20px" };
const RESUME_BUTTON_STYLE = {
	backgroundColor: "#E23F1C",
	border: "none",
	borderRadius: "4px",
	marginTop: "-6px",
	color: "#fff",
};
const PROFILE_IMAGE_STYLE = { width: "100px" };

const STANDARDS_CARDS = [
	{ name: "Organic NPOP", bgColor: "#C78B62" },
	{ name: "GlobalG.A.P.", bgColor: "#198754" },
	{ name: "ISO 9001", bgColor: "#FFC107" },
	{ name: "USFDA PCQI", bgColor: "#7805B5" },
	{ name: "ISO 14001", bgColor: "#BF67A8" },
	{ name: "FSSAI", bgColor: "#0D6EFD" },
	{ name: "FSSC 22000", bgColor: "#0DCAF0" },
	{ name: "ISO 22000", bgColor: "#C78B62" },
	{ name: "USDA NOP", bgColor: "#4C87B8" },
];

const Placementmanagement = () => {
	const dispatch = useDispatch();
	const { auth } = useSelector((state) => state);
	const downloadPdf = usePdfDownloader();

	// State
	const [pendingjobseekers, setPendingJobseekers] = useState([]);
	const [jobseekerInterest, setJobseekerInterest] = useState([]);
	const [jobseekerDetails, setJobseekerDetails] = useState({});

	// Fetch data
	useEffect(() => {
		const fetchData = async () => {
			if (!auth.token) return;

			try {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

				const [pendingRes, detailsRes, interestRes] = await Promise.all([
					getDataAPI(`getAllPendingJobseekers`, auth.token),
					getDataAPI(`getJobseekersCount`, auth.token),
					getDataAPI(`getAllJobseekerInterest`),
				]);

				setPendingJobseekers(pendingRes.data.jobseekers || []);
				setJobseekerDetails(detailsRes.data.jobseekersdetails || {});
				setJobseekerInterest(interestRes.data.jobseekerInterests || []);
			} catch (err) {
				console.error("Error fetching job seekers data:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: { error: "Failed to fetch job seekers data" },
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		};

		fetchData();
	}, [auth.token, dispatch]);

	// Accept handler
	const handleAcceptClick = useCallback(
		async (user) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI(
					"updatejobseekers",
					{
						id: user.id,
						activationstatus: "accepted",
					},
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error accepting job seeker:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error: err.response?.data?.msg || "Error accepting job seeker",
					},
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Decline handler
	const handleDeclineClick = useCallback(
		async (user) => {
			dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: true } });

			try {
				const res = await postDataAPI(
					"updatejobseekers",
					{
						id: user.id,
						activationstatus: "declined",
					},
					auth.token
				);

				if (res.status === 200) {
					window.location.reload();
				}
			} catch (err) {
				console.error("Error declining job seeker:", err);
				dispatch({
					type: GLOBALTYPES.ALERT,
					payload: {
						error: err.response?.data?.msg || "Error declining job seeker",
					},
				});
			} finally {
				dispatch({ type: GLOBALTYPES.ALERT, payload: { loading: false } });
			}
		},
		[auth.token, dispatch]
	);

	// Helper function to find count
	const findCount = useCallback(
		(name) => {
			const job = jobseekerInterest.find(
				(user) => user.f_s_s_intersted === name
			);
			return job ? job.count : 0;
		},
		[jobseekerInterest]
	);

	// Memoized components
	const statsCards = useMemo(
		() => [
			{
				key: "total",
				label: "Total Jobseekers",
				style: STATS_CARD_STYLE,
				value: jobseekerDetails.total || 0,
			},
			{
				key: "accepted",
				label: "Accepted Jobseekers",
				style: ACCEPTED_CARD_STYLE,
				value: jobseekerDetails.accepted || 0,
			},
			{
				key: "declined",
				label: "Declined Jobseekers",
				style: DECLINED_CARD_STYLE,
				value: jobseekerDetails.declined || 0,
			},
			{
				key: "male",
				label: "Male Jobseekers",
				style: MALE_CARD_STYLE,
				value: jobseekerDetails.male || 0,
			},
			{
				key: "female",
				label: "Female Jobseekers",
				style: FEMALE_CARD_STYLE,
				value: jobseekerDetails.female || 0,
			},
			{
				key: "pending",
				label: "Pending Jobseekers",
				style: PENDING_CARD_STYLE,
				value: jobseekerDetails.pending || 0,
			},
		],
		[jobseekerDetails]
	);

	const standardsCards = useMemo(() => {
		return STANDARDS_CARDS.map((standard) => ({
			...standard,
			count: findCount(standard.name),
		}));
	}, [findCount]);

	const jobseekersRows = useMemo(() => {
		return pendingjobseekers.map((jobseeker, index) => (
			<tr key={jobseeker.id}>
				<td>{index + 1}</td>
				<td>
					<img
						src={jobseeker.profilepic || "../images/common-user.svg"}
						alt=""
						style={PROFILE_IMAGE_STYLE}
					/>
				</td>
				<td>{jobseeker.name}</td>
				<td>{jobseeker.email}</td>
				<td>{jobseeker.gender}</td>
				<td>{jobseeker.mobile}</td>
				<td>{jobseeker.city + ", " + jobseeker.country}</td>
				<td>
					{jobseeker.resume && (
						<>
							<span>
								<Button
									onClick={() => downloadPdf(jobseeker.resume)}
									style={RESUME_BUTTON_STYLE}>
									Open
								</Button>
							</span>
							<br />
						</>
					)}
				</td>
				<td>{jobseeker.linkedin_profile}</td>
				<td>{jobseeker.availability}</td>
				<td>{jobseeker.yearsofexperience}</td>
				<td>{jobseeker.f_s_s_intersted}</td>
				<td>
					<button
						onClick={() => handleAcceptClick(jobseeker)}
						className="btn btn-success bg-success me-2">
						Accept
					</button>
					<button
						onClick={() => handleDeclineClick(jobseeker)}
						className="btn btn-danger bg-danger">
						Decline
					</button>
				</td>
			</tr>
		));
	}, [pendingjobseekers, downloadPdf, handleAcceptClick, handleDeclineClick]);

	if (!auth.user) {
		return <LoginRequest />;
	}

	return (
		<>
			<Adminnavbar />
			<div style={CONTAINER_STYLE}>
				<div className="dashboard-text">Super Admin Dashboard</div>
				<div id="admin">
					<div>
						<div className="row format">
							<Sidenavbar />
							<div className="col-8" style={DASHBOARD_COL_STYLE}>
								<div className="row format" style={DASHBOARD_ROW_STYLE}>
									{statsCards.slice(0, 3).map((card) => (
										<div
											key={card.key}
											className="col text-format"
											style={card.style}>
											<div className="text1">{card.value}</div>
											<div className="text1">{card.label}</div>
										</div>
									))}
								</div>
								<div className="row format" style={{ paddingBottom: "20px" }}>
									{statsCards.slice(3).map((card) => (
										<div
											key={card.key}
											className="col text-format"
											style={card.style}>
											<div className="text1">{card.value}</div>
											<div className="text1">{card.label}</div>
										</div>
									))}
								</div>
							</div>
							<div
								className="row format"
								style={{ paddingBottom: "20px", marginTop: "20px" }}>
								<div
									className="col card-format-analatics"
									style={ANALYTICS_CARD_STYLE}>
									<h4 className="instructorlist-text p-2">
										Jobseekers interested standards
									</h4>
									<div className="row format" style={ANALYTICS_ROW_STYLE}>
										{standardsCards.slice(0, 3).map((standard) => (
											<div
												key={standard.name}
												className="col text-format"
												style={{ backgroundColor: standard.bgColor }}>
												<div className="text1">{standard.count}</div>
												<div className="text1">{standard.name}</div>
											</div>
										))}
									</div>
									<div
										className="row format"
										style={ANALYTICS_BOTTOM_ROW_STYLE}>
										{standardsCards.slice(3, 6).map((standard) => (
											<div
												key={standard.name}
												className="col text-format"
												style={{ backgroundColor: standard.bgColor }}>
												<div className="text1">{standard.count}</div>
												<div className="text1">{standard.name}</div>
											</div>
										))}
									</div>
									<div
										className="row format"
										style={ANALYTICS_BOTTOM_ROW_STYLE}>
										{standardsCards.slice(6).map((standard) => (
											<div
												key={standard.name}
												className="col text-format"
												style={{ backgroundColor: standard.bgColor }}>
												<div className="text1">{standard.count}</div>
												<div className="text1">{standard.name}</div>
											</div>
										))}
									</div>
								</div>
							</div>
							<div className="row" style={TABLE_CONTAINER_STYLE}>
								<div className="col">
									<h4 className="instructorlist-text">Pending Jobseekers</h4>
									<table className="table table-responsive">
										<thead id="pl">
											<tr>
												<th>S.No</th>
												<th>Profile pic</th>
												<th>Name</th>
												<th>Email</th>
												<th>Gender</th>
												<th>Mobile</th>
												<th>Location</th>
												<th>Resume</th>
												<th>LinkedIn Profile</th>
												<th>Availability</th>
												<th>Years of Experience</th>
												<th>Food safety standard interested</th>
												<th>Action</th>
											</tr>
										</thead>
										<tbody>{jobseekersRows}</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer />
		</>
	);
};

export default Placementmanagement;
